import React, { useState, useEffect, Fragment } from 'react';
import { Dialog, Transition } from '@headlessui/react';
import { XMarkIcon, ExclamationTriangleIcon } from '@heroicons/react/24/outline';
import api from '../services/api';
import RichTextEditor from './RichTextEditor';
import { formatDateTimeForInput, datetimeLocalToUTC } from '../utils/dateHelpers';
import DateTimePickerInput from './DateTimePickerInput';
import { useDateFormatter } from '../hooks/useDateFormatter';

interface ChangeStatusModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSuccess: (updatedChange: any) => void;
  changeId: string;
  currentState: string;
  targetState: string;
  actionLabel: string;
  currentChange?: any;
}

interface FormData {
  justification: string;
  implementationPlan: string;
  riskImpactAnalysis: string;
  backoutPlan: string;
  testPlan: string;
  plannedStartDate: string;
  plannedEndDate: string;
  actualStartDate: string;
  actualEndDate: string;
  implementationResults: string;
  reviewResults: string;
  closureNotes: string;
  cancellationReason: string;
}

const CHANGE_STATE_LABELS = {
  new: 'New',
  assess: 'Assess',
  authorize: 'Authorize',
  scheduled: 'Scheduled',
  implement: 'Implement',
  review: 'Review',
  closed: 'Closed',
  cancelled: 'Cancelled'
};

const ChangeStatusModal: React.FC<ChangeStatusModalProps> = ({
  isOpen,
  onClose,
  onSuccess,
  changeId,
  currentState,
  targetState,
  actionLabel,
  currentChange
}) => {
  const { preferences } = useDateFormatter();
  const [formData, setFormData] = useState<FormData>({
    justification: '',
    implementationPlan: '',
    riskImpactAnalysis: '',
    backoutPlan: '',
    testPlan: '',
    plannedStartDate: '',
    plannedEndDate: '',
    actualStartDate: '',
    actualEndDate: '',
    implementationResults: '',
    reviewResults: '',
    closureNotes: '',
    cancellationReason: ''
  });

  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Reset form when modal opens/closes and pre-populate with existing values
  useEffect(() => {
    if (isOpen) {
      // Helper function to format datetime for input (convert UTC to local)
      const formatDateForInput = (dateString: string | null) => {
        return formatDateTimeForInput(dateString);
      };

      setFormData({
        justification: currentChange?.justification || '',
        implementationPlan: currentChange?.implementationPlan || '',
        riskImpactAnalysis: currentChange?.riskImpactAnalysis || '',
        backoutPlan: currentChange?.backoutPlan || '',
        testPlan: currentChange?.testPlan || '',
        plannedStartDate: formatDateForInput(currentChange?.plannedStartDate),
        plannedEndDate: formatDateForInput(currentChange?.plannedEndDate),
        actualStartDate: formatDateForInput(currentChange?.actualStart),
        actualEndDate: formatDateForInput(currentChange?.actualEnd),
        implementationResults: currentChange?.implementationResults || '',
        reviewResults: currentChange?.reviewResults || '',
        closureNotes: currentChange?.closureNotes || '',
        cancellationReason: currentChange?.cancellationReason || ''
      });
      setError(null);
    }
  }, [isOpen, currentChange]);

  const handleInputChange = (field: keyof FormData, value: any) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  const getRequiredFields = (): (keyof FormData)[] => {
    switch (targetState) {
      case 'authorize':
        // When moving from assess to authorize, require all planning fields
        if (currentState === 'assess') {
          return ['justification', 'implementationPlan', 'riskImpactAnalysis', 'backoutPlan', 'testPlan'];
        }
        return [];
      case 'scheduled':
        // Planned dates were already captured during assess phase, just need confirmation
        return [];
      case 'implement':
        // When moving to implement state, require implementation results
        return ['implementationResults'];
      case 'review':
        // When moving to review state, require review results
        return ['reviewResults'];
      case 'closed':
        // When closing a change, require actual dates and closure notes
        return ['actualStartDate', 'actualEndDate', 'closureNotes'];
      case 'cancelled':
        // When cancelling a change, require cancellation reason
        return ['cancellationReason'];
      default:
        return [];
    }
  };

  const validateForm = (): string[] => {
    const errors: string[] = [];
    const requiredFields = getRequiredFields();

    requiredFields.forEach(field => {
      const value = formData[field];
      if (!value || (typeof value === 'string' && !value.trim())) {
        const fieldLabels = {
          justification: 'Justification',
          implementationPlan: 'Implementation Plan',
          riskImpactAnalysis: 'Risk & Impact Analysis',
          backoutPlan: 'Backout Plan',
          testPlan: 'Test Plan',
          plannedStartDate: 'Planned Start Date',
          plannedEndDate: 'Planned End Date',
          actualStartDate: 'Actual Start Date',
          actualEndDate: 'Actual End Date',
          implementationResults: 'Implementation Results',
          reviewResults: 'Review Results',
          closureNotes: 'Closure Notes',
          cancellationReason: 'Cancellation Reason'
        };
        errors.push(`${fieldLabels[field]} is required`);
      }
    });

    // Date validation removed for scheduled state - dates already captured during assess phase

    // Additional validation for date/time fields when closing
    if (targetState === 'closed' && formData.actualStartDate && formData.actualEndDate) {
      const startDate = new Date(formData.actualStartDate);
      const endDate = new Date(formData.actualEndDate);

      if (endDate <= startDate) {
        errors.push('Actual End Date/Time must be after Actual Start Date/Time');
      }
    }

    return errors;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    const validationErrors = validateForm();
    if (validationErrors.length > 0) {
      setError(validationErrors.join(', '));
      return;
    }

    setLoading(true);
    setError(null);

    try {
      // Prepare the data to send
      const requestData: any = {
        changeState: targetState
      };

      // Only include relevant fields based on target state
      const requiredFields = getRequiredFields();
      if (requiredFields.length > 0) {
        requiredFields.forEach(field => {
          if (field === 'plannedStartDate' || field === 'plannedEndDate' || field === 'actualStartDate' || field === 'actualEndDate') {
            // Convert datetime-local format to UTC ISO string using user's timezone
            requestData[field] = formData[field] ? datetimeLocalToUTC(formData[field], preferences.timezone) : formData[field];
          } else {
            requestData[field] = formData[field];
          }
        });
      }

      const response = await api.patch(`/changes/${changeId}/state`, requestData);
      
      onSuccess(response.data);
      onClose();
    } catch (error: any) {
      console.error('Error updating change status:', error);
      setError(error.response?.data?.message || 'Failed to update change status');
    } finally {
      setLoading(false);
    }
  };

  const renderFormFields = () => {
    const requiredFields = getRequiredFields();
    
    if (requiredFields.length === 0) {
      // Simple confirmation for transitions that don't require additional fields
      return (
        <div className="mt-4">
          <div className="flex items-center space-x-3">
            <ExclamationTriangleIcon className="h-6 w-6 text-amber-500" />
            <p className="text-sm text-gray-700">
              Are you sure you want to change the status from{' '}
              <span className="font-medium">{CHANGE_STATE_LABELS[currentState as keyof typeof CHANGE_STATE_LABELS]}</span>{' '}
              to{' '}
              <span className="font-medium">{CHANGE_STATE_LABELS[targetState as keyof typeof CHANGE_STATE_LABELS]}</span>?
            </p>
          </div>
        </div>
      );
    }

    // Render form fields for transitions that require additional information
    return (
      <div className="mt-4 space-y-6">
        <div className="bg-blue-50 border border-blue-200 rounded-md p-4">
          <p className="text-sm text-blue-800">
            {targetState === 'implement'
              ? 'Please provide the implementation results to mark this change as implemented:'
              : targetState === 'review'
              ? 'Please provide the review results to complete the change review:'
              : targetState === 'closed'
              ? 'Please provide the actual start and end dates and closure notes to complete this change request:'
              : 'The following fields are required to authorize this change request:'
            }
          </p>
        </div>

        <div className="space-y-6">
          {requiredFields.includes('justification') && (
            <div>
              <label htmlFor="justification" className="block text-sm font-medium text-gray-700 mb-2">
                Justification *
              </label>
              <RichTextEditor
                value={formData.justification}
                onChange={(value) => handleInputChange('justification', value)}
                placeholder="Provide justification for this change..."
                minHeight="120px"
              />
            </div>
          )}

          {requiredFields.includes('riskImpactAnalysis') && (
            <div>
              <label htmlFor="riskImpactAnalysis" className="block text-sm font-medium text-gray-700 mb-2">
                Risk & Impact Analysis *
              </label>
              <RichTextEditor
                value={formData.riskImpactAnalysis}
                onChange={(value) => handleInputChange('riskImpactAnalysis', value)}
                placeholder="Assess the risks and potential impact of this change..."
                minHeight="120px"
              />
            </div>
          )}

          {requiredFields.includes('implementationPlan') && (
            <div>
              <label htmlFor="implementationPlan" className="block text-sm font-medium text-gray-700 mb-2">
                Implementation Plan *
              </label>
              <RichTextEditor
                value={formData.implementationPlan}
                onChange={(value) => handleInputChange('implementationPlan', value)}
                placeholder="Describe the step-by-step implementation plan..."
                minHeight="120px"
              />
            </div>
          )}

          {requiredFields.includes('backoutPlan') && (
            <div>
              <label htmlFor="backoutPlan" className="block text-sm font-medium text-gray-700 mb-2">
                Backout Plan *
              </label>
              <RichTextEditor
                value={formData.backoutPlan}
                onChange={(value) => handleInputChange('backoutPlan', value)}
                placeholder="Describe the procedure to revert this change if needed..."
                minHeight="120px"
              />
            </div>
          )}

          {requiredFields.includes('testPlan') && (
            <div>
              <label htmlFor="testPlan" className="block text-sm font-medium text-gray-700 mb-2">
                Test Plan *
              </label>
              <RichTextEditor
                value={formData.testPlan}
                onChange={(value) => handleInputChange('testPlan', value)}
                placeholder="Outline the testing procedures to validate this change..."
                minHeight="120px"
              />
            </div>
          )}
        </div>


        {/* Date fields for scheduling */}
        {targetState === 'scheduled' && (
          <div className="grid grid-cols-2 gap-6">
            <div>
              <label htmlFor="plannedStartDate" className="block text-sm font-medium text-gray-700 mb-2">
                Planned Start Date *
              </label>
              <DateTimePickerInput
                id="plannedStartDate"
                value={formData.plannedStartDate}
                onChange={(value) => handleInputChange('plannedStartDate', value)}
                required
              />
            </div>
            <div>
              <label htmlFor="plannedEndDate" className="block text-sm font-medium text-gray-700 mb-2">
                Planned End Date *
              </label>
              <DateTimePickerInput
                id="plannedEndDate"
                value={formData.plannedEndDate}
                onChange={(value) => handleInputChange('plannedEndDate', value)}
                min={formData.plannedStartDate || undefined}
                required
              />
            </div>
          </div>
        )}

        {/* Implementation Results field for implement transition */}
        {targetState === 'implement' && (
          <div>
            <label htmlFor="implementationResults" className="block text-sm font-medium text-gray-700 mb-2">
              Implementation Results *
            </label>
            <RichTextEditor
              value={formData.implementationResults}
              onChange={(value) => handleInputChange('implementationResults', value)}
              placeholder="Describe the results of the implementation, including any issues encountered, outcomes achieved, and validation performed..."
              minHeight="120px"
            />
          </div>
        )}

        {/* Review Results field for review transition */}
        {targetState === 'review' && (
          <div>
            <label htmlFor="reviewResults" className="block text-sm font-medium text-gray-700 mb-2">
              Review Results *
            </label>
            <RichTextEditor
              value={formData.reviewResults}
              onChange={(value) => handleInputChange('reviewResults', value)}
              placeholder="Provide detailed review results including verification of implementation, testing outcomes, and any issues identified..."
              minHeight="120px"
            />
          </div>
        )}

        {/* Actual dates and Closure Notes for closed transition */}
        {targetState === 'closed' && (
          <div className="space-y-6">
            <div className="grid grid-cols-2 gap-6">
              <div>
                <label htmlFor="actualStartDate" className="block text-sm font-medium text-gray-700 mb-2">
                  Actual Start Date *
                </label>
                <DateTimePickerInput
                  id="actualStartDate"
                  value={formData.actualStartDate}
                  onChange={(value) => handleInputChange('actualStartDate', value)}
                  required
                />
              </div>
              <div>
                <label htmlFor="actualEndDate" className="block text-sm font-medium text-gray-700 mb-2">
                  Actual End Date *
                </label>
                <DateTimePickerInput
                  id="actualEndDate"
                  value={formData.actualEndDate}
                  onChange={(value) => handleInputChange('actualEndDate', value)}
                  min={formData.actualStartDate || undefined}
                  required
                />
              </div>
            </div>
            <div>
              <label htmlFor="closureNotes" className="block text-sm font-medium text-gray-700 mb-2">
                Closure Notes *
              </label>
              <RichTextEditor
                value={formData.closureNotes}
                onChange={(value) => handleInputChange('closureNotes', value)}
                placeholder="Provide detailed closure notes including final outcomes, lessons learned, and any post-implementation observations..."
                minHeight="120px"
              />
            </div>
          </div>
        )}

        {/* Cancellation Reason for cancelled transition */}
        {targetState === 'cancelled' && (
          <div>
            <label htmlFor="cancellationReason" className="block text-sm font-medium text-gray-700 mb-2">
              Cancellation Reason *
            </label>
            <RichTextEditor
              value={formData.cancellationReason}
              onChange={(value) => handleInputChange('cancellationReason', value)}
              placeholder="Please provide a detailed reason for cancelling this change..."
              minHeight="120px"
            />
          </div>
        )}
      </div>
    );
  };

  return (
    <Transition appear show={isOpen} as={Fragment}>
      <Dialog as="div" className="relative z-50" onClose={onClose}>
        <Transition.Child
          as={Fragment}
          enter="ease-out duration-300"
          enterFrom="opacity-0"
          enterTo="opacity-100"
          leave="ease-in duration-200"
          leaveFrom="opacity-100"
          leaveTo="opacity-0"
        >
          <div className="fixed inset-0 bg-black bg-opacity-25" />
        </Transition.Child>

        <div className="fixed inset-0 overflow-y-auto">
          <div className="flex min-h-full items-center justify-center p-4 text-center">
            <Transition.Child
              as={Fragment}
              enter="ease-out duration-300"
              enterFrom="opacity-0 scale-95"
              enterTo="opacity-100 scale-100"
              leave="ease-in duration-200"
              leaveFrom="opacity-100 scale-100"
              leaveTo="opacity-0 scale-95"
            >
              <Dialog.Panel className="w-full max-w-4xl transform overflow-hidden rounded-2xl bg-white p-6 text-left align-middle shadow-xl transition-all">
                <div className="flex items-center justify-between">
                  <Dialog.Title as="h3" className="text-lg font-medium leading-6 text-gray-900">
                    {actionLabel}
                  </Dialog.Title>
                  <button
                    type="button"
                    className="rounded-md text-gray-400 hover:text-gray-500 focus:outline-none focus:ring-2 focus:ring-indigo-500"
                    onClick={onClose}
                  >
                    <XMarkIcon className="h-6 w-6" />
                  </button>
                </div>

                <form onSubmit={handleSubmit}>
                  {renderFormFields()}

                  {error && (
                    <div className="mt-4 p-3 bg-red-50 border border-red-200 rounded-md">
                      <p className="text-sm text-red-800">{error}</p>
                    </div>
                  )}

                  <div className="mt-6 flex justify-end space-x-3">
                    <button
                      type="button"
                      className="px-4 py-2 text-sm font-medium text-gray-700 bg-gray-100 border border-gray-300 rounded-md hover:bg-gray-200 focus:outline-none focus:ring-2 focus:ring-indigo-500"
                      onClick={onClose}
                      disabled={loading}
                    >
                      Cancel
                    </button>
                    <button
                      type="submit"
                      className="px-4 py-2 text-sm font-medium text-white bg-indigo-600 border border-transparent rounded-md hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-indigo-500 disabled:opacity-50"
                      disabled={loading}
                    >
                      {loading ? 'Processing...' : actionLabel}
                    </button>
                  </div>
                </form>
              </Dialog.Panel>
            </Transition.Child>
          </div>
        </div>
      </Dialog>
    </Transition>
  );
};

export default ChangeStatusModal;